// Importazioni necessarie
import { Injectable } from '@angular/core';
import { UserComplete } from '../interfaces/user.interfaces';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Tracking } from './business-data.service';

// Definizione delle interfacce
type DataStore = Record<string, ManifestDetail[] | Manifest[] | Warehouse[] | User[] | string[] | any>;

export interface ManifestDetail {
  manifestCode: string;
  summary: ManifestSummary;
  waybills: TrackingWaybill[];
}

export interface ManifestSummary {
  title: string;
  description: string;
  date: string;
  location: string;
  timeStart: string;
  waybills: number;
  parcels: number;
  note: string;
  status: string;
  warehouse: string;
}

export interface TrackingWaybill {
  waybillId: number;
  waybillCode: string;
  waybillPalletCode: string;
  waybillShipmentCode: string;
  waybillDate: string; // ISO string
  waybillEstimatedDeliveryDate: string;
  waybillActualDeliveryDate: string;
  waybillDeliveryMethod: string;
  waybillDeliveryType: string;
  waybillTransitTimeHours: number;
  waybillTotalWeight: number;
  waybillTotalVolume: number;
  waybillBrandCode: string;
  waybillDealerCode: string;
  waybillCarrierCode: string;
  waybillOriginLocation: string;
  waybillDestinationLocation: string;
  waybillTrackingNumber: string;
  waybillLastKnownLocation: string;
  waybillStatus: string;
  waybillFragile: string;
  waybillHazardousMaterial: string;
  waybillCustomsClearanceRequired: string;
  waybillUpdatedAt: string;
  waybillUpdatedBy: string;
  waybillNote: string;
  waybillFile: string;
  waybillVersion: number;
  waybillCreatedAt: string;
}

export interface TrackingParcel {
  parcelId: number;
  parcelCode: string;
  parcelDate: string;
  parcelDeliveryDate: string;
  parcelEstimatedDeliveryDate: string;
  parcelDeliveryStatus: string;
  parcelDeliveryMethod: string;
  parcelDeliveryType: string;
  parcelDeliveryMaterial: string;
  parcelWaybillCode: string;
  parcelPalletCode: string;
  parcelShipmentCode: string;
  parcelBrandCode: string;
  parcelClientCode: string;
  parcelDealerCode: string;
  parcelDescription: string;
  parcelWeight: number;
  parcelVolume: number;
  parcelLength: number;
  parcelWidth: number;
  parcelHeight: number;
  parcelType: string;
  parcelBarcode: string;
  parcelTrackingNumber: string;
  parcelFragile: string;
  parcelHazardousMaterial: string;
  parcelTemperatureSensitive: string;
  parcelInsuredValue: number;
  parcelLocation: string;
  parcelStatus: string;
  parcelVersion: number;
  parcelLastUpdateAt: string;
  parcelLastUpdateBy: string;
  parcelHighValueItem: string;
  parcelRestrictedItem: string;
  parcelCustomsClearanceRequired: string;
  parcelUpdatedAt: string;
  parcelUpdatedBy: string;
  parcelCreatedAt: string;
  parcelLatitude: number;
  parcelLongitude: number;
  parcelOriginalDeliveryDate: string;
  parcelOriginalLatitude: number;
  parcelOriginalLongitude: number;
}

export interface Manifest {
  title: string;
  code: string;
  description: string;
  date: string;
  location: string;
  timeStart: string;
  waybills: number;
  parcels: number;
  note: string;
  status: string;
  warehouse: string;
  actions: ManifestActions;
  emails: string[];
}

export interface ManifestActions {
  canPrint: boolean;
  canSendEmail: boolean;
  canConfigure: boolean;
}

export interface Warehouse {
  name: string;
  location?: string;
  capacity?: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private data: DataStore = {};

  constructor(private http: HttpClient) {
    this.initializeData(); // 👈 Inizializza i dati ogni volta che l’app riparte
  }

  getData<T>(key: string): T | undefined {
    return this.data[key] as T;
  }

  setData<T>(key: string, value: T): void {
    this.data[key] = value;
  }

  updateData<T>(key: string, partialValue: Partial<T>): void {
    if (this.data[key]) {
      this.data[key] = { ...this.data[key], ...partialValue };
    } else {
      console.warn(`Tentativo di aggiornamento dati inesistenti per la chiave: ${key}`);
      this.data[key] = partialValue;
    }
  }

  clearData(key?: string): void {
    if (key) {
      delete this.data[key];
    } else {
      this.data = {};
    }
  }

  hasData(key: string): boolean {
    return this.data.hasOwnProperty(key);
  }

  getAllKeys(): string[] {
    return Object.keys(this.data);
  }

  getAllData(): DataStore {
    return { ...this.data };
  }

  resetData(newData: DataStore): void {
    this.data = { ...newData };
  }

  cloneData<T>(key: string): T | undefined {
    return this.data[key] ? JSON.parse(JSON.stringify(this.data[key])) : undefined;
  }

  mergeData(newData: DataStore): void {
    this.data = { ...this.data, ...newData };
  }

  countKeys(): number {
    return Object.keys(this.data).length;
  }

  getTrackingByWayBill(trackingNumberWayBill: string) {
    this.http.get<Tracking[]>(`${environment.apiUrl}/tracking/${trackingNumberWayBill}`).subscribe({
      next: (tracking: Tracking[]) => {
        this.setData<Tracking[]>('tracking', tracking);
      },
      error: (err) => {
        console.error('Errore durante la chiamata GET tracking:', err);
      }
    });
  }

  initializeData(): void {

    // this.http.get<Manifest[]>(`${environment.apiUrl}/manifests`).subscribe({
    //   next: (manifests: Manifest[]) => {
    //     this.setData<Manifest[]>('manifests', manifests);
    //   },
    //   error: (err) => {
    //     console.error('Errore durante la chiamata GET manifests:', err);
    //   }
    // });


    // this.setData<Manifest[]>('manifests', [
    //   {
    //     title: 'TOUR - 1',
    //     code: 'TOUR-001-2025-04-01-VERONA-001',
    //     description: 'Tour per la Lombardia',
    //     date: '2025-05-28',
    //     location: 'Verona',
    //     timeStart: '19:00',
    //     waybills: 8,
    //     parcels: 20,
    //     note: 'Nessuna',
    //     status: 'COMPLETED',
    //     warehouse: 'Warehouse A',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },

    //   {
    //     title: 'TOUR - 3',
    //     code: 'TOUR-003-2025-04-03-FIRENZE-003',
    //     description: 'Tour attivo in Toscana',
    //     date: '2025-05-28',
    //     location: 'Firenze',
    //     timeStart: '10:00',
    //     waybills: 7,
    //     parcels: 15,
    //     note: 'Nessuna',
    //     status: 'ACTIVE',
    //     warehouse: 'Warehouse B',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 4',
    //     code: 'TOUR-004-2025-04-04-PALERMO-004',
    //     description: 'Tour inattivo in Sicilia',
    //     date: '2025-05-28',
    //     location: 'Palermo',
    //     timeStart: '09:00',
    //     waybills: 4,
    //     parcels: 10,
    //     note: 'Nessuna',
    //     status: 'INACTIVE',
    //     warehouse: 'Warehouse C',
    //     actions: {
    //       canPrint: false,
    //       canSendEmail: false,
    //       canConfigure: false
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 5',
    //     code: 'TOUR-005-2025-04-05-VENEZIA-005',
    //     description: 'Tour in transito in Veneto',
    //     date: '2025-04-05',
    //     location: 'Venezia',
    //     timeStart: '12:00',
    //     waybills: 6,
    //     parcels: 18,
    //     note: 'Nessuna',
    //     status: 'IN_TRANSIT',
    //     warehouse: 'Warehouse D',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 6',
    //     code: 'TOUR-006-2025-04-06-BOLONIA-006',
    //     description: 'Tour consegnato in Emilia-Romagna',
    //     date: '2025-04-06',
    //     location: 'Bologna',
    //     timeStart: '14:00',
    //     waybills: 3,
    //     parcels: 8,
    //     note: 'Nessuna',
    //     status: 'DELIVERED',
    //     warehouse: 'Warehouse E',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: false
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 7',
    //     code: 'TOUR-007-2025-04-07-NAPOLI-007',
    //     description: 'Tour restituito in Campania',
    //     date: '2025-04-07',
    //     location: 'Napoli',
    //     timeStart: '16:00',
    //     waybills: 2,
    //     parcels: 5,
    //     note: 'Nessuna',
    //     status: 'RETURNED',
    //     warehouse: 'Warehouse A',
    //     actions: {
    //       canPrint: false,
    //       canSendEmail: false,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 8',
    //     code: 'TOUR-008-2025-04-08-BARI-008',
    //     description: 'Tour fallito in Puglia',
    //     date: '2025-04-08',
    //     location: 'Bari',
    //     timeStart: '18:00',
    //     waybills: 1,
    //     parcels: 3,
    //     note: 'Problemi tecnici',
    //     status: 'FAILED',
    //     warehouse: 'Warehouse B',
    //     actions: {
    //       canPrint: false,
    //       canSendEmail: false,
    //       canConfigure: false
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 9',
    //     code: 'TOUR-009-2025-04-09-CAGLIARI-009',
    //     description: 'Tour in sospeso in Sardegna',
    //     date: '2025-04-09',
    //     location: 'Cagliari',
    //     timeStart: '20:00',
    //     waybills: 9,
    //     parcels: 25,
    //     note: 'In attesa di conferma',
    //     status: 'ON_HOLD',
    //     warehouse: 'Warehouse C',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: false,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 10',
    //     code: 'TOUR-010-2025-04-10-REGGIO-CALABRIA-010',
    //     description: 'Tour in attesa in Calabria',
    //     date: '2025-04-10',
    //     location: 'Reggio Calabria',
    //     timeStart: '07:00',
    //     waybills: 10,
    //     parcels: 30,
    //     note: 'Nessuna',
    //     status: 'PENDING',
    //     warehouse: 'Warehouse D',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 11',
    //     code: 'TOUR-011-2025-04-11-PERUGIA-011',
    //     description: 'Tour annullato in Umbria',
    //     date: '2025-05-28',
    //     location: 'Perugia',
    //     timeStart: '11:00',
    //     waybills: 0,
    //     parcels: 0,
    //     note: 'Cancellato per maltempo',
    //     status: 'CANCELLED',
    //     warehouse: 'Warehouse E',
    //     actions: {
    //       canPrint: false,
    //       canSendEmail: false,
    //       canConfigure: false
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 12',
    //     code: 'TOUR-012-2025-04-12-GENOVA-012',
    //     description: 'Tour partito in Liguria',
    //     date: '2025-05-28',
    //     location: 'Genova',
    //     timeStart: '13:00',
    //     waybills: 12,
    //     parcels: 40,
    //     note: 'Nessuna',
    //     status: 'STARTED',
    //     warehouse: 'Warehouse A',
    //     actions: {
    //       canPrint: true,
    //       canSendEmail: true,
    //       canConfigure: true
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   },
    //   {
    //     title: 'TOUR - 13',
    //     code: 'TOUR-013-2025-04-13-AOSTA-013',
    //     description: 'Tour vuoto in Valle d\'Aosta',
    //     date: '2025-05-28',
    //     location: 'Aosta',
    //     timeStart: '15:00',
    //     waybills: 0,
    //     parcels: 0,
    //     note: 'Nessuna',
    //     status: 'EMPTY',
    //     warehouse: 'Warehouse B',
    //     actions: {
    //       canPrint: false,
    //       canSendEmail: false,
    //       canConfigure: false
    //     },
    //     emails: [
    //       'example1@example.com',
    //       'example2@example.com',
    //       'example3@example.com'
    //     ]
    //   }
    // ]);

    // this.setData<ManifestDetail[]>('manifest_details', [
    //   {
    //     manifestCode: 'TOUR-001-2025-04-01-VERONA-001',
    //     summary: {
    //       title: 'TOUR - 1',
    //       description: 'Tour per la Lombardia',
    //       date: '2025-05-28',
    //       location: 'Verona',
    //       timeStart: '19:00',
    //       waybills: 8,
    //       parcels: 20,
    //       note: 'Nessuna',
    //       status: 'COMPLETED',
    //       warehouse: 'Warehouse A'
    //     },
    //     waybills: [
    //       {
    //         code: 'WB-001',
    //         recipient: 'Mario Rossi',
    //         address: 'Via Roma 10, Verona',
    //         phone: '+39 3456789123',
    //         parcels: [
    //           { code: 'P-001', weight: '2kg', size: '30x20x10cm', content: 'Elettronica' },
    //           { code: 'P-002', weight: '1kg', size: '25x15x10cm', content: 'Libri' }
    //         ]
    //       },
    //       {
    //         code: 'WB-002',
    //         recipient: 'Giulia Bianchi',
    //         address: 'Via Milano 5, Verona',
    //         phone: '+39 3471122334',
    //         parcels: [
    //           { code: 'P-003', weight: '3kg', size: '40x30x20cm', content: 'Abbigliamento' }
    //         ]
    //       }
    //     ]
    //   },
    //   {
    //     manifestCode: 'TOUR-003-2025-04-03-FIRENZE-003',
    //     summary: {
    //       title: 'TOUR - 3',
    //       description: 'Tour attivo in Toscana',
    //       date: '2025-05-28',
    //       location: 'Firenze',
    //       timeStart: '10:00',
    //       waybills: 7,
    //       parcels: 15,
    //       note: 'Nessuna',
    //       status: 'ACTIVE',
    //       warehouse: 'Warehouse B'
    //     },
    //     waybills: [
    //       {
    //         code: 'WB-010',
    //         recipient: 'Luca Neri',
    //         address: 'Piazza Duomo 1, Firenze',
    //         phone: '+39 3467890123',
    //         parcels: [
    //           { code: 'P-010', weight: '2.5kg', size: '35x25x15cm', content: 'Calzature' }
    //         ]
    //       }
    //     ]
    //   },
    //   {
    //     manifestCode: 'TOUR-004-2025-04-04-PALERMO-004',
    //     summary: {
    //       title: 'TOUR - 4',
    //       description: 'Tour inattivo in Sicilia',
    //       date: '2025-05-28',
    //       location: 'Palermo',
    //       timeStart: '09:00',
    //       waybills: 4,
    //       parcels: 10,
    //       note: 'Nessuna',
    //       status: 'INACTIVE',
    //       warehouse: 'Warehouse C'
    //     },
    //     waybills: [
    //       {
    //         code: 'WB-020',
    //         recipient: 'Antonio Verdi',
    //         address: 'Via Libertà 12, Palermo',
    //         phone: '+39 3456677889',
    //         parcels: [
    //           { code: 'P-020', weight: '5kg', size: '50x40x30cm', content: 'Prodotti Alimentari' },
    //           { code: 'P-021', weight: '1.5kg', size: '20x15x10cm', content: 'Cosmetici' }
    //         ]
    //       }
    //     ]
    //   }
    // ]);

    this.setData<Warehouse[]>('warehouses', [
      { name: 'Warehouse A' },
      { name: 'Warehouse B' },
      { name: 'Warehouse C' },
      { name: 'Warehouse D' },
      { name: 'Warehouse E' }
    ]);    this.mergeData({
      warehouses: [
        ...(this.getData<Warehouse[]>('warehouses') || []),
        { name: 'Warehouse F' },
        { name: 'Warehouse G' },
        { name: 'Warehouse H' },
        { name: 'Warehouse I' }
      ]
    });
    
    // Aggiungi dati utente mockati
    this.setData<UserComplete[]>('users', [
      {
        userId: 1001,
        userCode: 'USR1001',
        userName: 'Rossi Mario',
        userEmail: 'mario.rossi@example.com',
        phoneNumber: '+39 345 678 9012',
        userAddressStreet: 'Via Roma',
        userAddressNumber: '123',
        userAddressZip: '00100',
        userAddressCity: 'Roma',
        userAddressProvince: 'RM',
        userAddressCountry: 'Italia',
        userLanguage: 'it',
        userNotes: 'Account aziendale',
        userRegistrationDate: '2024-01-15T10:30:00Z',
        userLastLoginDate: '2025-05-20T08:45:00Z',
        userStatus: 'ACTIVE',
        userType: 'ADMIN',
        userAffiliationType: 'COMPANY',
        userAffiliationCode: 'COMP-001',
        userVersion: 1,
        userCreatedAt: '2024-01-15T10:30:00Z'
      }
    ]);
  }
}